// Item definitions ported from Java Item.java
// Weapons (melee/ranged) and armor with stats and prices

import { GAME_CONSTANTS } from './constants';

export const ITEM_TYPE = {
  MELEE_WEAPON: 0,
  RANGED_WEAPON: 1,
  ARMOUR: 2,
} as const;

export const WEAPON_SUBTYPE = {
  SPEAR: 0,
  SWORD: 1,
  AXE: 2,
  MACE: 3,
  BOW: 4,
  CROSSBOW: 5,
} as const;

export type ItemType = typeof ITEM_TYPE[keyof typeof ITEM_TYPE];
export type WeaponSubtype = typeof WEAPON_SUBTYPE[keyof typeof WEAPON_SUBTYPE];

export interface Item {
  id: string;
  name: string;
  type: ItemType;
  subtype?: WeaponSubtype;
  price: number;
  dmg?: string;        // dice roll for damage
  hitBonus?: number;   // melee weapon attack bonus
  blockBonus?: number; // melee weapon block bonus
  protection?: number; // armor protection value
  discounted?: boolean;
}

// Weapon subtype names in Finnish
export const SUBTYPE_NAMES: Record<WeaponSubtype, string> = {
  [WEAPON_SUBTYPE.SPEAR]: 'Keihäs',
  [WEAPON_SUBTYPE.SWORD]: 'Miekka',
  [WEAPON_SUBTYPE.AXE]: 'Kirves',
  [WEAPON_SUBTYPE.MACE]: 'Nuija',
  [WEAPON_SUBTYPE.BOW]: 'Jousi',
  [WEAPON_SUBTYPE.CROSSBOW]: 'Varsijousi',
};

// Helper to create melee weapon
function melee(
  name: string,
  subtype: WeaponSubtype,
  price: number,
  dmg: string,
  hitBonus: number,
  blockBonus: number
): Item {
  return {
    id: name.toLowerCase().replace(/\s+/g, '-'),
    name,
    type: ITEM_TYPE.MELEE_WEAPON,
    subtype,
    price,
    dmg,
    hitBonus,
    blockBonus,
  };
}

// Helper to create ranged weapon
function ranged(name: string, subtype: WeaponSubtype, price: number, dmg: string): Item {
  return {
    id: name.toLowerCase().replace(/\s+/g, '-'),
    name,
    type: ITEM_TYPE.RANGED_WEAPON,
    subtype,
    price,
    dmg,
  };
}

// Helper to create armor
function armor(name: string, price: number, protection: number): Item {
  return {
    id: name.toLowerCase().replace(/\s+/g, '-'),
    name,
    type: ITEM_TYPE.ARMOUR,
    price,
    protection,
  };
}

// Spears
export const SPEARS: Item[] = [
  melee('Terävä keppi', WEAPON_SUBTYPE.SPEAR, 40, '1d4+1', 0, 0),
  melee('Kivikeihäs', WEAPON_SUBTYPE.SPEAR, 102, '1d6+1', 0, 5),
  melee('Lyhyt keihäs', WEAPON_SUBTYPE.SPEAR, 205, '1d8+1', 0, 10),
  melee('Pitkä keihäs', WEAPON_SUBTYPE.SPEAR, 328, '1d10+1', 5, 10),
  melee('Suurkeihäs', WEAPON_SUBTYPE.SPEAR, 683, '2d6+2', 5, 15),
  melee('Jalkaväen peitsi', WEAPON_SUBTYPE.SPEAR, 822, '3d6', 20, 0),
  melee('Mystinen keihäs', WEAPON_SUBTYPE.SPEAR, 1195, '1d8+8', 15, 10),
  melee('Tulikeihäs', WEAPON_SUBTYPE.SPEAR, 1504, '3d6+4', 25, 0),
  melee('Siunattu keihäs', WEAPON_SUBTYPE.SPEAR, 2433, '3d8+5', 5, 25),
  melee('Mahtikeihäs', WEAPON_SUBTYPE.SPEAR, 3534, '2d10+10', 10, 30),
];

// Swords
export const SWORDS: Item[] = [
  melee('Tikari', WEAPON_SUBTYPE.SWORD, 71, '1d4+1', 5, 0),
  melee('Lyhyt Miekka', WEAPON_SUBTYPE.SWORD, 160, '1d6+1', 5, 5),
  melee('Sapeli', WEAPON_SUBTYPE.SWORD, 230, '1d6+2', 10, 0),
  melee('Leveä Miekka', WEAPON_SUBTYPE.SWORD, 360, '1d8+2', 10, 5),
  melee('Bastardimiekka', WEAPON_SUBTYPE.SWORD, 376, '1d10+2', 5, 5),
  melee('Suurmiekka', WEAPON_SUBTYPE.SWORD, 683, '2d6+2', 15, 0),
  melee('Musta Miekka', WEAPON_SUBTYPE.SWORD, 1195, '1d8+8', 20, 0),
  melee('Siunattu miekka', WEAPON_SUBTYPE.SWORD, 1672, '2d8+4', 10, 20),
  melee('Tulimiekka', WEAPON_SUBTYPE.SWORD, 2270, '3d8+3', 20, 10),
  melee('Mahtimiekka', WEAPON_SUBTYPE.SWORD, 3946, '2d10+10', 20, 20),
];

// Axes
export const AXES: Item[] = [
  melee('Käsikirves', WEAPON_SUBTYPE.AXE, 51, '1d4+1', 0, 0),
  melee('Kirves', WEAPON_SUBTYPE.AXE, 129, '1d6+1', 5, 0),
  melee('Taistelukirves', WEAPON_SUBTYPE.AXE, 409, '1d8+2', 10, 5),
  melee('Suurkirves', WEAPON_SUBTYPE.AXE, 598, '2d6', 15, 5),
  melee('Piilukirves', WEAPON_SUBTYPE.AXE, 871, '2d6+2', 20, 0),
  melee('Hilpari', WEAPON_SUBTYPE.AXE, 1026, '3d6', 15, 5),
  melee('Lumottu kirves', WEAPON_SUBTYPE.AXE, 1638, '1d8+8', 20, 10),
  melee('Mestarikirves', WEAPON_SUBTYPE.AXE, 2073, '3d6+4', 15, 15),
  melee('Siunattu kirves', WEAPON_SUBTYPE.AXE, 2475, '2d6+8', 10, 25),
  melee('Mahtikirves', WEAPON_SUBTYPE.AXE, 4382, '2d10+10', 25, 15),
];

// Maces
export const MACES: Item[] = [
  melee('Puunuija', WEAPON_SUBTYPE.MACE, 25, '1d4', 0, 0),
  melee('Pamppu', WEAPON_SUBTYPE.MACE, 94, '1d6', 5, 0),
  melee('Nuija', WEAPON_SUBTYPE.MACE, 205, '1d8', 5, 5),
  melee('Raskas nuija', WEAPON_SUBTYPE.MACE, 360, '1d10', 10, 5),
  melee('Moukari', WEAPON_SUBTYPE.MACE, 577, '2d6', 15, 0),
  melee('Sotamoukari', WEAPON_SUBTYPE.MACE, 1137, '3d6', 15, 5),
  melee('Lumottu nuija', WEAPON_SUBTYPE.MACE, 1849, '1d8+8', 20, 10),
  melee('Muinainen nuija', WEAPON_SUBTYPE.MACE, 1886, '2d6+6', 25, 0),
  melee('Siunattu nuija', WEAPON_SUBTYPE.MACE, 3635, '3d6+8', 10, 25),
  melee('Mahtimoukari', WEAPON_SUBTYPE.MACE, 4898, '2d10+10', 30, 10),
];

// Bows
export const BOWS: Item[] = [
  ranged('Lyhytjousi', WEAPON_SUBTYPE.BOW, 172, '1d4+1'),
  ranged('Örkkijousi', WEAPON_SUBTYPE.BOW, 306, '1d6+1'),
  ranged('Pitkäjousi', WEAPON_SUBTYPE.BOW, 478, '1d8+1'),
  ranged('Sarvijousi', WEAPON_SUBTYPE.BOW, 688, '1d10+1'),
  ranged('Haltiajousi', WEAPON_SUBTYPE.BOW, 937, '1d12+1'),
  ranged('Mestarijousi', WEAPON_SUBTYPE.BOW, 1550, '2d6+2'),
  ranged('Lumottu jousi', WEAPON_SUBTYPE.BOW, 1913, '1d10+5'),
  ranged('Pyhä haltiajousi', WEAPON_SUBTYPE.BOW, 2755, '3d6+2'),
  ranged('Siunattu jousi', WEAPON_SUBTYPE.BOW, 3233, '1d12+7'),
  ranged('Mahtijousi', WEAPON_SUBTYPE.BOW, 4898, '2d10+5'),
];

// Crossbows
export const CROSSBOWS: Item[] = [
  ranged('Kevyt varsijousi', WEAPON_SUBTYPE.CROSSBOW, 107, '1d4+2'),
  ranged('Kevyt kääpiövarsijousi', WEAPON_SUBTYPE.CROSSBOW, 172, '2d3+1'),
  ranged('Varsijousi', WEAPON_SUBTYPE.CROSSBOW, 234, '1d8+2'),
  ranged('Kääpiövarsijousi', WEAPON_SUBTYPE.CROSSBOW, 552, '3d4+2'),
  ranged('Raskas varsijousi', WEAPON_SUBTYPE.CROSSBOW, 808, '2d6+4'),
  ranged('Pyhä kääpiövarsijousi', WEAPON_SUBTYPE.CROSSBOW, 971, '4d4+2'),
  ranged('Lumottu varsijousi', WEAPON_SUBTYPE.CROSSBOW, 1550, '2d6+8'),
  ranged('Muinainen varsijousi', WEAPON_SUBTYPE.CROSSBOW, 1727, '3d6+6'),
  ranged('Siunattu varsijousi', WEAPON_SUBTYPE.CROSSBOW, 2211, '2d8+9'),
  ranged('Mahtivarsijousi', WEAPON_SUBTYPE.CROSSBOW, 2989, '2d10+10'),
];

// Armors
export const ARMORS: Item[] = [
  armor('Nahkanuttu', 32, 1),
  armor('Besaittuhaarniska', 128, 2),
  armor('Silmukkahaarniska', 288, 3),
  armor('Rengashaarniska', 512, 4),
  armor('Suomuhaarniska', 800, 5),
  armor('Levyhaarniska', 1152, 6),
  armor('Mystinen haarniska', 1568, 7),
  armor('Mithrilhaarniska', 2048, 8),
  armor('Siunattu haarniska', 3200, 10),
  armor('Mahtihaarniska', 4608, 12),
];

// All melee weapons combined
export const MELEE_WEAPONS: Item[] = [...SPEARS, ...SWORDS, ...AXES, ...MACES];

// All ranged weapons combined
export const RANGED_WEAPONS: Item[] = [...BOWS, ...CROSSBOWS];

// All items combined
export const ALL_ITEMS: Item[] = [...MELEE_WEAPONS, ...RANGED_WEAPONS, ...ARMORS];

// Get items by subtype
export function getItemsBySubtype(subtype: WeaponSubtype): Item[] {
  switch (subtype) {
    case WEAPON_SUBTYPE.SPEAR:
      return SPEARS;
    case WEAPON_SUBTYPE.SWORD:
      return SWORDS;
    case WEAPON_SUBTYPE.AXE:
      return AXES;
    case WEAPON_SUBTYPE.MACE:
      return MACES;
    case WEAPON_SUBTYPE.BOW:
      return BOWS;
    case WEAPON_SUBTYPE.CROSSBOW:
      return CROSSBOWS;
    default:
      return [];
  }
}

// Price calculations
export function getNormalPrice(item: Item): number {
  return item.price;
}

export function getDiscountedPrice(item: Item): number {
  return Math.round(item.price * GAME_CONSTANTS.DISCOUNT_PRICE);
}

export function getSellPrice(item: Item): number {
  return Math.round(item.price * GAME_CONSTANTS.SELL_PRICE);
}

export function getCurrentPrice(item: Item): number {
  return item.discounted ? getDiscountedPrice(item) : getNormalPrice(item);
}

// Find item by name
export function findItemByName(name: string): Item | undefined {
  return ALL_ITEMS.find(item => item.name === name);
}

// Find item by ID
export function findItemById(id: string): Item | undefined {
  return ALL_ITEMS.find(item => item.id === id);
}

// Generate discounted items (one from each category)
export function generateDiscountedItems(): Item[] {
  const discounted: Item[] = [];
  const categories = [SPEARS, SWORDS, AXES, MACES, BOWS, CROSSBOWS, ARMORS];

  for (const category of categories) {
    const randomItem = category[Math.floor(Math.random() * category.length)];
    discounted.push({ ...randomItem, discounted: true });
  }

  return discounted;
}
